import { css } from "@zenchef/styled-system/css";
import Illustrations, { IllustrationProps } from "./Illustrations";
import {
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  type SVGProps,
} from "react";

export const artNames = [
  "Ai",
  "Bread",
  "Brocoli",
  "ChefHat",
  "Cocktail",
  "Coffee",
  "Drink01",
  "Drink02",
  "Drink03",
  "Error",
  "FinishedPlate",
  "FoodDetails",
  "Fork",
  "IceCream",
  "Laptop",
  "Loading",
  "LoyaltyCard",
  "Note",
  "Oyster",
  "PaperMenu",
  "Pastries",
  "Payment",
  "Plate",
  "Reserve",
  "Service",
  "ServiceWine",
  "Sharing",
  "Stars",
  "Table",
  "Toast",
] as const satisfies (keyof typeof Illustrations)[];
export type ArtName = (typeof artNames)[number];

export interface ArtProps extends IllustrationProps {
  name: ArtName;
  layout: "square" | "vertical" | "horizontal";
}

const resetSizeSvg = (svgEl: SVGSVGElement, fEl: SVGForeignObjectElement) => {
  fEl.removeAttribute("width");
  fEl.removeAttribute("height");
  svgEl.setAttribute("viewBox", "0 0 400 400");
};

const sizeSVG = (
  svgEl: SVGSVGElement,
  boxEl: HTMLDivElement,
  fEl: SVGForeignObjectElement,
) => {
  // Get the bounds of the SVG content
  const bbox = {
    x: boxEl.clientLeft,
    y: boxEl.clientTop,
    width: boxEl.clientWidth,
    height: boxEl.clientHeight,
  };
  // Set the viewport with these bounds
  fEl.setAttribute("width", `${bbox.width}`);
  fEl.setAttribute("height", `${bbox.height}`);
  svgEl.setAttribute(
    "viewBox",
    `${bbox.x} ${bbox.y} ${bbox.width} ${bbox.height}`,
  );
};

const Art = ({ name, layout, ...restProps }: ArtProps) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const fRef = useRef<SVGForeignObjectElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const layoutProps: SVGProps<SVGSVGElement> = {};
  switch (layout) {
    case "square": {
      layoutProps.width = 400;
      layoutProps.height = 400;
      break;
    }
    case "vertical": {
      layoutProps.width = 400;
      break;
    }
    case "horizontal": {
      layoutProps.height = 400;
    }
  }

  const resize = () => {
    if (!svgRef.current) return;
    if (!boxRef.current) return;
    if (!fRef.current) return;

    sizeSVG(svgRef.current, boxRef.current, fRef.current);
  };

  useLayoutEffect(() => {
    if (!svgRef.current) return;
    if (!fRef.current) return;
    resetSizeSvg(svgRef.current, fRef.current);
  }, [name, layout, restProps]);

  useEffect(() => {
    resize();

    if (!svgRef.current) return;

    const observer = new MutationObserver(() => {
      resize();
    });

    observer.observe(svgRef.current, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
    };
  }, [svgRef, boxRef, fRef, name, layout, restProps]);

  const Illustration = Illustrations[name];

  return (
    <svg ref={svgRef} {...layoutProps} {...restProps} viewBox="0 0 400 400">
      <foreignObject ref={fRef}>
        <div
          ref={boxRef}
          className={css({
            width: 400,
            height: layout === "vertical" ? "fit-content" : 400,
            display: "flex",
            flexDirection: layout !== "horizontal" ? "column" : "row",
            alignItems: "center",
            justifyContent: "center",
          })}
        >
          <Suspense fallback={null}>
            <Illustration />
          </Suspense>
        </div>
      </foreignObject>
    </svg>
  );
};

export default Art;
